import React, { useState } from "react";
import { useTable } from "@/lib/store";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHead, Chip, Empty, Bar, Modal, Field, Area, Pick, Kicker, SectionHead, Avatar } from "@/components/kit";
import { ROMAN, currentPct, statusColor, reportStreak } from "@/lib/util";
import { Standings, MemberMomentum, Badges, StreakChip } from "@/components/Momentum";

const Grid = ({ goal, canEdit, refresh }) => {
  const [editing, setEditing] = useState(null);
  const [val, setVal] = useState("");
  const p = goal.progress || {};

  const save = async (key) => {
    try {
      await api.put(`/goals/${goal.id}/progress`, { key, value: val === "" ? null : Number(val) });
      setEditing(null); setVal("");
      await refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid grid-cols-8 gap-1 mt-4" data-testid={`goal-grid-${goal.id}`}>
      {ROMAN.map((r, i) => {
        const key = `m${i + 1}`;
        const v = p[key];
        const filled = v !== undefined && v !== null && v !== "";
        if (editing === key) {
          return (
            <input
              key={key}
              autoFocus
              type="number"
              min="0"
              max="100"
              data-testid={`goal-cell-input-${goal.id}-${key}`}
              className="field !p-0 text-center text-xs h-9"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              onBlur={() => save(key)}
              onKeyDown={(e) => e.key === "Enter" && save(key)}
            />
          );
        }
        return (
          <button
            key={key}
            disabled={!canEdit}
            data-testid={`goal-cell-${goal.id}-${key}`}
            onClick={() => { setEditing(key); setVal(filled ? String(v) : ""); }}
            className="h-9 rounded-md text-xs flex flex-col items-center justify-center"
            style={{
              border: `1px solid ${filled ? "var(--brass-bright)" : "var(--hairline)"}`,
              background: filled ? "rgba(37,99,235,.10)" : "transparent",
              color: filled ? "var(--ink)" : "var(--ink-faint)",
              cursor: canEdit ? "pointer" : "default",
            }}
          >
            <span className="serif leading-none text-[10px]" style={{ color: "var(--brass)" }}>{r}</span>
            <span className="leading-none mt-0.5">{filled ? `${v}` : "—"}</span>
          </button>
        );
      })}
    </div>
  );
};

const AvgChart = ({ goals }) => {
  const avgs = ROMAN.map((_, i) => {
    const vals = goals.map((g) => g.progress?.[`m${i + 1}`]).filter((v) => v !== undefined && v !== null && v !== "");
    return vals.length ? Math.round(vals.reduce((a, b) => a + Number(b), 0) / vals.length) : null;
  });
  return (
    <Card data-testid="forum-average-chart">
      <SectionHead>Forum average by chapter</SectionHead>
      <div className="flex items-stretch gap-2 sm:gap-3 h-44 mt-2">
        {avgs.map((a, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-2.5">
            <div
              className="w-full flex items-end flex-1 min-h-0 rounded-[7px] overflow-hidden"
              style={{ background: "rgba(15,23,42,.04)", border: "1px solid var(--hairline-soft)" }}
            >
              <div
                className="w-full relative"
                style={{
                  height: `${a || 0}%`,
                  background:
                    a === null
                      ? "transparent"
                      : "linear-gradient(180deg, var(--brass-bright), var(--brass))",
                  minHeight: a === null ? 0 : 3,
                  transition: "height .7s var(--ease)",
                }}
              />
            </div>
            <span className="serif text-sm leading-none" style={{ color: "var(--brass)" }}>
              {ROMAN[i]}
            </span>
            <span className="numeral text-[11px]" style={{ color: "var(--ink-mute)" }}>
              {a === null ? "—" : `${a}%`}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default function Goals() {
  const { me, goals, activeMembers, yearId, refresh, canWrite } = useTable();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ goal: "", measurement: "", category: "Professional", status: "Not started", memberId: "" });
  const [editId, setEditId] = useState(null);

  const ordered = [
    ...activeMembers.filter((m) => m.id === me.memberId),
    ...activeMembers.filter((m) => m.id !== me.memberId),
  ];

  const submit = async () => {
    try {
      if (editId) await api.put(`/goals/${editId}`, form);
      else await api.post("/goals", { ...form, yearId, memberId: form.memberId || me.memberId });
      setOpen(false); setEditId(null);
      setForm({ goal: "", measurement: "", category: "Professional", status: "Not started", memberId: "" });
      await refresh();
      toast.success("Saved.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="goals-page">
      <PageHead
        kicker="Measurable"
        title="Goals"
        sub="Two goals per member — one professional, one personal. Progress is claimed out loud, chapter by chapter. Points come from reporting, gaining ground and finishing."
        right={canWrite && (
          <button data-testid="add-goal" className="btn-ink" onClick={() => { setEditId(null); setOpen(true); }}>Add goal</button>
        )}
      />

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 items-start">
        <AvgChart goals={goals} />
        <Standings members={activeMembers} goals={goals} meId={me.memberId} />
      </div>

      <div className="mt-10 space-y-10">
        {ordered.map((m) => {
          const mine = goals.filter((g) => g.memberId === m.id);
          const canEdit = canWrite && (me.isAdmin || m.id === me.memberId);
          return (
            <div key={m.id} data-testid={`goals-member-${m.id}`}>
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Avatar name={m.name} color={m.avatarColor} size={34} />
                <h2 className="serif text-[1.6rem] leading-none">{m.name}</h2>
                {m.id === me.memberId && <Chip color="var(--brass)">You</Chip>}
                <div className="hidden sm:block flex-1 hair mx-2" />
                <MemberMomentum member={m} goals={mine} />
              </div>
              {mine.length === 0 ? (
                <Empty line="No goals set. The year needs a number." />
              ) : (
                <div className="grid md:grid-cols-2 gap-5 stagger">
                  {mine.map((g) => {
                    const pct = currentPct(g);
                    return (
                      <Card key={g.id} lift data-testid={`goal-card-${g.id}`}>
                        <div className="flex justify-between gap-3 items-start">
                          <Chip color={g.category === "Personal" ? "var(--blue)" : "var(--brass)"}>{g.category}</Chip>
                          <Chip color={statusColor(g.status)}>{g.status}</Chip>
                        </div>
                        <h3 className="serif text-xl mt-3 leading-snug">{g.goal}</h3>
                        {g.measurement && (
                          <p className="text-xs mt-2" style={{ color: "var(--ink-mute)" }}>{g.measurement}</p>
                        )}
                        <div className="mt-4 flex items-center gap-3">
                          <div className="flex-1"><Bar value={pct || 0} color={g.status === "At risk" ? "var(--rust)" : "var(--green)"} /></div>
                          <span className="serif text-lg w-12 text-right" data-testid={`goal-pct-${g.id}`}>
                            {pct === null ? "—" : `${pct}%`}
                          </span>
                        </div>
                        <Grid goal={g} canEdit={canEdit} refresh={refresh} />
                        <div className="flex items-center gap-2 mt-4 flex-wrap">
                          <Badges goal={g} testId={`badges-${g.id}`} />
                          <StreakChip n={reportStreak(g)} testId={`goal-streak-${g.id}`} />
                        </div>
                        {canEdit && (
                          <div className="flex gap-2 mt-4">
                            <button
                              className="btn-quiet"
                              data-testid={`edit-goal-${g.id}`}
                              onClick={() => {
                                setEditId(g.id);
                                setForm({ goal: g.goal, measurement: g.measurement, category: g.category, status: g.status, memberId: g.memberId });
                                setOpen(true);
                              }}
                            >
                              Edit
                            </button>
                            <button
                              className="btn-quiet"
                              data-testid={`delete-goal-${g.id}`}
                              onClick={async () => { await api.delete(`/goals/${g.id}`); await refresh(); }}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit goal" : "New goal"} testId="goal-modal">
        <Area label="The goal" value={form.goal} data-testid="goal-text" onChange={(e) => setForm({ ...form, goal: e.target.value })} />
        <Area label="How it is measured" value={form.measurement} data-testid="goal-measurement" onChange={(e) => setForm({ ...form, measurement: e.target.value })} />
        <Pick label="Category" options={["Professional", "Personal"]} value={form.category} data-testid="goal-category" onChange={(e) => setForm({ ...form, category: e.target.value })} />
        <Pick label="Status" options={["Not started", "On track", "At risk", "Done"]} value={form.status} data-testid="goal-status" onChange={(e) => setForm({ ...form, status: e.target.value })} />
        {me.isAdmin && !editId && (
          <label className="block">
            <span className="kicker block mb-1.5">Member</span>
            <select className="field" data-testid="goal-member" value={form.memberId} onChange={(e) => setForm({ ...form, memberId: e.target.value })}>
              <option value="">Me</option>
              {activeMembers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </label>
        )}
        <button className="btn-ink w-full" data-testid="goal-save" onClick={submit}>Save</button>
      </Modal>
    </div>
  );
}
